import React, { useState, useEffect } from "react";
import { defaultUpdateInventoryItem, UpdateMaterialInventoryPayload } from "../../types/InventoryItem";
import FormComponent from "../generic/FormComponent";
import RowItem from "../generic/RowItem";
import AddAnotherItemButton from "../generic/AddAnotherItemButton";
import SubmitButton from "../generic/SubmitButton";
import ButtonGroupOrganizer from "../generic/ButtonGroupOrganizer";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { updateInventory } from "../../services/MaterialAPI";
import { catchError, handleInventoryUpdateResponseAlerts, renderInvalidFormErrors } from "../../utils/AlertUtils";
import MaterialTypeSelector from "../shared/MaterialTypeSelector";
import QuantityUnitDropdown from "../shared/QuantityUnitDropdown";
import InputField from "../generic/InputField";

const QUANTITY_TYPES = {
    CHANGED: "quantity_changed",
    REMAINING: "remaining_quantity",
};

const LABELS = {
    QUANTITY_CHANGED: "Quantity Changed",
    REMAINING_QUANTITY: "Remaining Quantity",
};

const UpdateInventoryForm: React.FC = () => {
    const [items, setItems] = useState<Array<UpdateMaterialInventoryPayload>>([{ ...defaultUpdateInventoryItem }]);
    const [successAlert, setSuccessAlert] = useState<{ message: string } | null>(null);
    const [errorAlert, setErrorAlert] = useState<{ message: string } | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (items.length === 0) {
            setItems([{ ...defaultUpdateInventoryItem }]);
        }
    }, [items]);

    const handleFieldChange = (index: number, field: string, value: string | number | boolean | null) => {
        setItems((prevItems) => {
            const updatedItems = [...prevItems];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            return updatedItems;
        });
        clearAlerts();
    };

    const clearAlerts = () => {
        setSuccessAlert(null);
        setErrorAlert(null);
    };

    const handleSubmit = async () => {
        if (!isFormValid()) {
            setErrorAlert({ message: "Please fill in all required fields correctly." });
            return;
        }
        setIsLoading(true);

        try {
            const response = await updateInventory(items.map(item => ({
                material_type_id: item.material_type_id!,
                material_size_id: item.material_size_id!,
                color_code: item.color_code?.trim() || "",
                quantity_unit: item.quantity_unit!,
                ...(item.quantity_type === QUANTITY_TYPES.CHANGED
                    ? { quantity_changed: Number(item.quantity_changed), remaining_quantity: undefined }
                    : { remaining_quantity: Number(item.remaining_quantity), quantity_changed: undefined }),
            })));

            handleInventoryUpdateResponseAlerts(response, setSuccessAlert, setErrorAlert);

            if (response.errors && response.errors.length > 0) {
                setItems(response.errors.map((error: any) => ({
                    material_type_id: error.record?.material_type_id || 0,
                    material_size_id: error.record?.material_size_id || 0,
                    color_code: error.record?.color_code || "",
                    quantity_changed: error.record?.quantity_type === QUANTITY_TYPES.CHANGED ? error.record?.quantity_changed || 0 : undefined,
                    remaining_quantity: error.record?.quantity_type === QUANTITY_TYPES.REMAINING ? error.record?.remaining_quantity || 0 : undefined,
                    quantity_unit: error.record?.quantity_unit || null,
                    quantity_type: error.record?.quantity_type || QUANTITY_TYPES.CHANGED,
                })));
            } else {
                setItems([{ ...defaultUpdateInventoryItem }]);
                setErrorAlert(null);
            }
        } catch (error) {
            catchError(error, setErrorAlert);
        } finally {
            setIsLoading(false);
        }
    };

    const addNewItem = () => {
        setItems([...items, { ...defaultUpdateInventoryItem }]);
    };

    const removeItem = (index: number) => {
        setItems((prevItems) => {
            const updatedItems = prevItems.filter((_, i) => i !== index);
            return updatedItems.length > 0 ? updatedItems : [{ ...defaultUpdateInventoryItem }];
        });
    };

    const isFormValid = (): boolean => {
        return items.every(item =>
            item.material_type_id !== null &&
            item.material_size_id !== null &&
            !!item.color_code.trim() &&
            item.quantity_unit !== null &&
            (Number(item.quantity_changed) > 0 || Number(item.remaining_quantity) > 0 )
        );
    };

    return (
        <FormComponent isFullScreen={true} title={"Use Materials"}>
            <SharedAlertBanner
                successAlert={successAlert}
                errorAlert={errorAlert}
                onCloseSuccess={() => setSuccessAlert(null)}
                onCloseError={() => setErrorAlert(null)}
            />
            {items.map((item, index) => (
                <RowItem key={index} index={index} showRemoveButton={items.length > 1} onRemove={removeItem}>
                    <MaterialTypeSelector
                        onSelect={({ material_type_id, color_code, material_size_id }) => {
                            handleFieldChange(index, "material_type_id", material_type_id ?? 0);
                            handleFieldChange(index, "material_size_id", material_size_id ?? 0);
                            handleFieldChange(index, "color_code", color_code || "");
                        }}
                    />
                    <QuantityUnitDropdown
                        value={item.quantity_unit ?? ""}
                        onChange={(value) => handleFieldChange(index, "quantity_unit", value)}
                    />
                    <div className="flex gap-4 items-center my-4">
                        {[QUANTITY_TYPES.CHANGED, QUANTITY_TYPES.REMAINING].map((type) => (
                            <label
                                className="radio flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-200 tracking-wide"
                                key={type}
                            >
                                <input
                                    type="radio"
                                    name={`quantity_type_${index}`}
                                    value={type}
                                    checked={item.quantity_type === type}
                                    onChange={(e) => handleFieldChange(index, "quantity_type", e.target.value)}
                                    className="form-radio h-4 w-4 text-blue-600 focus:ring-blue-500 dark:focus:ring-gray-400"
                                />
                                <span>
                                    {type === QUANTITY_TYPES.CHANGED
                                        ? LABELS.QUANTITY_CHANGED
                                        : LABELS.REMAINING_QUANTITY}
                                </span>
                            </label>
                        ))}
                    </div>
                    {item.quantity_type === QUANTITY_TYPES.CHANGED && (
                        <InputField
                            label={LABELS.QUANTITY_CHANGED}
                            type="number"
                            value={item.quantity_changed ?? ""}
                            onChange={(value) => handleFieldChange(index, "quantity_changed", value)}
                            placeholder="Enter quantity changed"
                            required
                        />
                    )}

                    {item.quantity_type === QUANTITY_TYPES.REMAINING && (
                        <InputField
                            label={LABELS.REMAINING_QUANTITY}
                            type="number"
                            value={item.remaining_quantity ?? ""}
                            onChange={(value) => handleFieldChange(index, "remaining_quantity", value)}
                            placeholder="Enter remaining quantity"
                            required
                        />
                    )}
                </RowItem>
            ))}
            <ButtonGroupOrganizer>
                <AddAnotherItemButton onAdd={addNewItem} />
                <SubmitButton isLoading={isLoading} disabled={!isFormValid()} onClick={handleSubmit} />
            </ButtonGroupOrganizer>
        </FormComponent>
    );
};

export default UpdateInventoryForm;